import type { ListMeta } from '@porotutu/react/paging'
import { sortDirection } from '@porotutu/react/paging'
import { createLink, Link, useRouterState } from '@tanstack/react-router'
import type { ComponentProps, ReactNode } from 'react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import styles from './DataTable.module.css'
import { useRowNavigation } from './useRowNavigation'

export type Column<T> = {
  key: string
  header: string
  cell: (row: T) => ReactNode
  sortKey?: string
  numeric?: boolean
}

export type DataTableProps<T> = {
  id?: string
  columns: Array<Column<T>>
  rows: T[]
  rowKey: (row: T) => string
  meta?: ListMeta
  empty: ReactNode
}

const ARROWS = { asc: '▲', desc: '▼' } as const

const SORTED = { asc: 'ascending', desc: 'descending' } as const

function ariaSort<T>(column: Column<T>, meta: ListMeta | undefined) {
  if (meta === undefined || column.sortKey === undefined) return undefined

  const direction = sortDirection(column.sortKey, meta)

  return direction === null ? undefined : SORTED[direction]
}

function SortLink({ column, header, meta }: { column: string; header: string; meta: ListMeta }) {
  const direction = sortDirection(column, meta)
  const next = meta.sort === column && meta.direction === 'desc' ? 'asc' : 'desc'

  return (
    <Link
      to="."
      className={styles.sort}
      search={(prev) => ({ ...prev, sort: column, direction: next, offset: undefined })}
      resetScroll={false}
    >
      {header}
      <span className={direction === null ? styles.arrow : styles.arrowActive} aria-hidden="true">
        {direction === null ? '⇅' : ARROWS[direction]}
      </span>
    </Link>
  )
}

function RowAnchor({ className, ...props }: ComponentProps<'a'>) {
  return <a className={className ? `${styles.rowLink} ${className}` : styles.rowLink} {...props} />
}

export const RowLink = createLink(RowAnchor)

export function PlainRowLink({ href, children }: { href: string; children: ReactNode }) {
  return <RowAnchor href={href}>{children}</RowAnchor>
}

export function DataTable<T>({ id, columns, rows, rowKey, meta, empty }: DataTableProps<T>) {
  const busy = useRouterState({ select: (state) => state.isLoading })
  const navigation = useRowNavigation()

  if (rows.length === 0) return <div id={id}>{empty}</div>

  return (
    <div id={id} className={busy ? styles.busy : undefined} aria-busy={busy || undefined}>
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((column) => (
              <TableHead
                key={column.key}
                className={column.numeric ? `${styles.head} ${styles.numeric}` : styles.head}
                aria-sort={ariaSort(column, meta)}
              >
                {column.sortKey && meta ? (
                  <SortLink column={column.sortKey} header={column.header} meta={meta} />
                ) : (
                  column.header
                )}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={rowKey(row)} className={styles.row} {...navigation}>
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  className={column.numeric ? `${styles.cell} ${styles.numeric}` : styles.cell}
                >
                  {column.cell(row)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
